import React, { useMemo, useState } from 'react';
import { Box, Typography, Tabs, Tab, Stack, Fade } from '@mui/material';
import { BatchSummary } from '../../services/api';
import TransformationCard from './TransformationCard';

interface TransformationPipelineProps {
  batches: BatchSummary[];
  needsAttentionBatches: BatchSummary[];
  inProgressBatches: BatchSummary[];
  completedBatches: BatchSummary[];
  onBootstrapFromProject?: (batchId: string) => void;
}

type FilterTab = 'all' | 'attention' | 'progress' | 'complete';

const TransformationPipeline: React.FC<TransformationPipelineProps> = ({
  batches,
  needsAttentionBatches,
  inProgressBatches,
  completedBatches,
  onBootstrapFromProject,
}) => {
  const [tab, setTab] = useState<FilterTab>('all');

  const sortedBatches = useMemo(() => {
    return [...batches].sort((a, b) => {
      const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
      const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
      return dateB - dateA;
    });
  }, [batches]);

  const filteredBatches = useMemo(() => {
    switch (tab) {
      case 'attention':
        return needsAttentionBatches;
      case 'progress':
        return inProgressBatches;
      case 'complete':
        return completedBatches;
      default:
        return sortedBatches;
    }
  }, [tab, sortedBatches, needsAttentionBatches, inProgressBatches, completedBatches]);

  if (batches.length === 0) return null;

  return (
    <Box sx={{ mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" fontWeight={600}>
          Your Transformations
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {batches.length} total
        </Typography>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 2, minHeight: 36 }}
        TabIndicatorProps={{ sx: { height: 2.5, borderRadius: 2 } }}
      >
        <Tab
          label="All"
          value="all"
          sx={{ minHeight: 36, textTransform: 'none', fontWeight: 500 }}
        />
        <Tab
          label={
            <Stack direction="row" spacing={0.5} alignItems="center">
              <span>Needs Attention</span>
              {needsAttentionBatches.length > 0 && (
                <Box
                  component="span"
                  sx={{
                    bgcolor: 'warning.main',
                    color: 'white',
                    borderRadius: '10px',
                    px: 0.8,
                    py: 0.1,
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    lineHeight: 1.4,
                  }}
                >
                  {needsAttentionBatches.length}
                </Box>
              )}
            </Stack>
          }
          value="attention"
          sx={{ minHeight: 36, textTransform: 'none', fontWeight: 500 }}
        />
        <Tab
          label="In Progress"
          value="progress"
          sx={{ minHeight: 36, textTransform: 'none', fontWeight: 500 }}
        />
        <Tab
          label="Complete"
          value="complete"
          sx={{ minHeight: 36, textTransform: 'none', fontWeight: 500 }}
        />
      </Tabs>

      {filteredBatches.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
          {tab === 'attention'
            ? 'No transformations need your attention right now.'
            : tab === 'progress'
              ? 'No transformations are in progress.'
              : tab === 'complete'
                ? 'No completed transformations yet.'
                : 'No transformations yet. Start by uploading a file.'}
        </Typography>
      ) : (
        <Stack spacing={2}>
          {filteredBatches.map((batch, i) => (
            <Fade in key={batch.batch_id} timeout={200 + i * 50}>
              <Box>
                <TransformationCard
                  batch={batch}
                  onBootstrapFromProject={onBootstrapFromProject}
                />
              </Box>
            </Fade>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default TransformationPipeline;
